import { useCallback, useEffect, useRef, useState } from "react";
import { Button, EmptyState, Icon, Input, Modal, Pill, Select, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { formatBytes, iconForType } from "./AttachmentList.js";
import { ScanBadge } from "./ScanBadge.js";
import { isOpenable, isPendingScan, useScanPolling } from "../lib/scan-status.js";
import { downloadFile, uploadFileWithProgress } from "../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../lib/upload-error.js";
import type { ViewedFile } from "./FileViewer.js";
import "./shared-folders.css";

type Folder = {
  id: string;
  name: string;
  kind: "shared" | "project";
  parentFolderId: string | null;
  projectId: string | null;
  ownerId: string;
  canWrite: boolean;
  canManage: boolean;
};
type FolderFile = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploaderName: string;
  scanStatus?: string;
};
type Member = { userId: string; role: "member" | "viewer"; displayName: string };
type Project = { id: string; name: string };
type Crumb = { id: string; name: string };

const PAGE = 50;

/**
 * Shared office folders and per-project folders.
 *
 * Distinct from the personal chips on the Files page: these carry access
 * (owner + member/viewer grants, or the project's own membership), so the
 * server decides what each person may do and this only reflects it
 * (`canWrite` / `canManage`). Files list here shows the same "Scanning…" badge
 * as everywhere else, and pages through `nextCursor`.
 */
export function SharedFolders({ onOpenFile }: { onOpenFile: (file: ViewedFile) => void }) {
  const { toast } = useToast();
  const [space, setSpace] = useState<"shared" | "project">("shared");
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState("");
  const [current, setCurrent] = useState<Folder | null>(null);
  const [path, setPath] = useState<Crumb[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersCursor, setFoldersCursor] = useState<string | null>(null);
  const [fileRows, setFileRows] = useState<FolderFile[]>([]);
  const [filesCursor, setFilesCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<Folder | null>(null);
  const [deleting, setDeleting] = useState<Folder | null>(null);
  const [sharing, setSharing] = useState<Folder | null>(null);
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api
      .get<{ projects: Project[] }>("/projects")
      .then((res) => setProjects(res.projects))
      .catch(() => undefined);
  }, []);

  const fail = useCallback(
    (error: unknown, fallback: string) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" }),
    [toast],
  );

  const foldersUrl = useCallback(
    (cursor?: string | null) => {
      const q = new URLSearchParams({ limit: String(PAGE) });
      if (current) q.set("parentId", current.id);
      else if (space === "project") q.set("projectId", projectId);
      if (cursor) q.set("cursor", cursor);
      return `/folders?${q.toString()}`;
    },
    [current, space, projectId],
  );

  const load = useCallback(async () => {
    if (!current && space === "project" && !projectId) {
      setFolders([]);
      setFileRows([]);
      setFoldersCursor(null);
      setFilesCursor(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [fl, fi] = await Promise.all([
        api.get<{ folders: Folder[]; nextCursor: string | null }>(foldersUrl()),
        current
          ? api.get<{ files: FolderFile[]; nextCursor: string | null }>(`/folders/${current.id}/files?limit=${PAGE}`)
          : Promise.resolve({ files: [] as FolderFile[], nextCursor: null }),
      ]);
      setFolders(fl.folders);
      setFoldersCursor(fl.nextCursor);
      setFileRows(fi.files);
      setFilesCursor(fi.nextCursor);
    } catch (error) {
      fail(error, "Could not load folders.");
    } finally {
      setLoading(false);
    }
  }, [current, space, projectId, foldersUrl, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  // Breadcrumb for the open folder.
  useEffect(() => {
    if (!current) {
      setPath([]);
      return;
    }
    api
      .get<{ path: Crumb[] }>(`/folders/${current.id}`)
      .then((res) => setPath(res.path))
      .catch(() => setPath([{ id: current.id, name: current.name }]));
  }, [current]);

  // "Scanning…" clears itself, as in the main list.
  const refreshStatuses = useCallback(() => {
    if (!current) return;
    api
      .get<{ files: FolderFile[] }>(`/folders/${current.id}/files?limit=${PAGE}`)
      .then((res) => {
        const status = new Map(res.files.map((f) => [f.id, f.scanStatus]));
        setFileRows((prev) => prev.map((f) => (status.has(f.id) ? { ...f, scanStatus: status.get(f.id) } : f)));
      })
      .catch(() => undefined);
  }, [current]);
  useScanPolling(fileRows.some(isPendingScan), refreshStatuses);

  const openCrumb = async (crumb: Crumb) => {
    try {
      const res = await api.get<{ folder: Folder }>(`/folders/${crumb.id}`);
      setCurrent(res.folder);
    } catch (error) {
      fail(error, "Could not open that folder.");
    }
  };

  const canCreateHere = current ? current.canWrite : space === "shared" || (space === "project" && !!projectId);

  const createFolder = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.post("/folders", {
        name: trimmed,
        ...(current ? { parentFolderId: current.id } : space === "project" ? { projectId } : {}),
      });
      setName("");
      setCreating(false);
      await load();
    } catch (error) {
      fail(error, "Could not create the folder.");
    } finally {
      setBusy(false);
    }
  };

  const renameFolder = async () => {
    if (!renaming || !name.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/folders/${renaming.id}`, { name: name.trim() });
      setRenaming(null);
      setName("");
      await load();
    } catch (error) {
      fail(error, "Could not rename the folder.");
    } finally {
      setBusy(false);
    }
  };

  const deleteFolder = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/folders/${deleting.id}`);
      setDeleting(null);
      await load();
    } catch (error) {
      // 409: not empty. The server's message says what to do.
      fail(error, "Could not delete the folder.");
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const loadMoreFolders = async () => {
    if (!foldersCursor) return;
    try {
      const res = await api.get<{ folders: Folder[]; nextCursor: string | null }>(foldersUrl(foldersCursor));
      setFolders((prev) => [...prev, ...res.folders]);
      setFoldersCursor(res.nextCursor);
    } catch (error) {
      fail(error, "Could not load more folders.");
    }
  };

  const loadMoreFiles = async () => {
    if (!current || !filesCursor) return;
    try {
      const res = await api.get<{ files: FolderFile[]; nextCursor: string | null }>(
        `/folders/${current.id}/files?limit=${PAGE}&cursor=${encodeURIComponent(filesCursor)}`,
      );
      setFileRows((prev) => [...prev, ...res.files]);
      setFilesCursor(res.nextCursor);
    } catch (error) {
      fail(error, "Could not load more files.");
    }
  };

  const upload = (file: File) => {
    if (!current) return;
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    uploadFileWithProgress("/files", form, () => undefined)
      .promise.then(async (result) => {
        if (!result.ok) throw uploadFailure(result);
        const uploaded = (result.body as { file: FolderFile }).file;
        await api.patch(`/files/${uploaded.id}`, { folderId: current.id });
        await load();
        toast(`${file.name} uploaded.`);
      })
      .catch((error: unknown) => toast(`${file.name}: ${uploadThrownMessage(error)}`, { tone: "danger" }))
      .finally(() => {
        setBusy(false);
        if (uploadRef.current) uploadRef.current.value = "";
      });
  };

  return (
    <div className="shared-folders">
      <div className="shared-folders__bar">
        <Select
          aria-label="Folder space"
          value={space}
          onChange={(event) => {
            setSpace(event.target.value === "project" ? "project" : "shared");
            setCurrent(null);
          }}
        >
          <option value="shared">Shared office folders</option>
          <option value="project">Project folders</option>
        </Select>
        {space === "project" && (
          <Select
            aria-label="Project"
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setCurrent(null);
            }}
          >
            <option value="">Choose a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        <span className="shared-folders__spacer" />
        {current && current.canWrite && (
          <>
            <input
              ref={uploadRef}
              type="file"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload(file);
              }}
            />
            <Button size="sm" variant="secondary" loading={busy} onClick={() => uploadRef.current?.click()}>
              Upload here
            </Button>
          </>
        )}
        {canCreateHere && (
          <Button size="sm" onClick={() => { setName(""); setCreating(true); }}>
            New folder
          </Button>
        )}
      </div>

      <nav className="shared-folders__crumbs" aria-label="Folder path">
        <button type="button" onClick={() => setCurrent(null)}>
          {space === "shared" ? "Shared folders" : "Project folders"}
        </button>
        {path.map((crumb) => (
          <span key={crumb.id}>
            <Icon name="chevron-right" size={12} />
            <button type="button" onClick={() => void openCrumb(crumb)} disabled={crumb.id === current?.id}>
              {crumb.name}
            </button>
          </span>
        ))}
        {current?.kind === "shared" && current.canManage && !current.parentFolderId && (
          <Button size="sm" variant="ghost" onClick={() => setSharing(current)}>
            Manage access
          </Button>
        )}
      </nav>

      {loading ? (
        <p className="shared-folders__note">Loading…</p>
      ) : folders.length === 0 && fileRows.length === 0 ? (
        <EmptyState
          icon="document"
          title={space === "project" && !projectId && !current ? "Pick a project" : "Nothing here yet"}
          description={current ? "This folder is empty." : "Folders you have access to will appear here."}
        />
      ) : (
        <ul className="shared-folders__list">
          {folders.map((folder) => (
            <li key={folder.id} className="shared-folders__row">
              <button type="button" className="shared-folders__name" onClick={() => setCurrent(folder)}>
                <Icon name="document" size={15} />
                <span>{folder.name}</span>
              </button>
              {!folder.canWrite && <Pill tone="neutral">Read only</Pill>}
              {(folder.parentFolderId ? folder.canWrite : folder.canManage) && (
                <span className="shared-folders__actions">
                  <Button size="sm" variant="ghost" onClick={() => { setName(folder.name); setRenaming(folder); }}>
                    Rename
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(folder)}>
                    Delete
                  </Button>
                </span>
              )}
            </li>
          ))}
          {foldersCursor && (
            <li>
              <Button size="sm" variant="ghost" onClick={() => void loadMoreFolders()}>
                Load more folders
              </Button>
            </li>
          )}
          {fileRows.map((file) => (
            <li key={file.id} className="shared-folders__row">
              {isOpenable(file) ? (
                <button
                  type="button"
                  className="shared-folders__name"
                  onClick={() => onOpenFile({ id: file.id, fileName: file.fileName, contentType: file.contentType })}
                >
                  <Icon name={iconForType(file.contentType)} size={15} />
                  <span>{file.fileName}</span>
                </button>
              ) : (
                <span className="shared-folders__name is-disabled">
                  <Icon name={iconForType(file.contentType)} size={15} />
                  <span>{file.fileName}</span>
                </span>
              )}
              <ScanBadge file={file} />
              <span className="shared-folders__meta">
                {formatBytes(file.sizeBytes)} · {file.uploaderName}
              </span>
              {isOpenable(file) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void downloadFile(`/api/v1/files/${file.id}/download`, file.fileName)}
                >
                  Download
                </Button>
              )}
            </li>
          ))}
          {filesCursor && (
            <li>
              <Button size="sm" variant="ghost" onClick={() => void loadMoreFiles()}>
                Load more files
              </Button>
            </li>
          )}
        </ul>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New folder">
        <Input aria-label="Folder name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus />
        <div className="shared-folders__modal-actions">
          <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
          <Button loading={busy} disabled={!name.trim()} onClick={() => void createFolder()}>Create</Button>
        </div>
      </Modal>

      <Modal open={renaming !== null} onClose={() => setRenaming(null)} title="Rename folder">
        <Input aria-label="Folder name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus />
        <div className="shared-folders__modal-actions">
          <Button variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
          <Button loading={busy} disabled={!name.trim()} onClick={() => void renameFolder()}>Rename</Button>
        </div>
      </Modal>

      <Modal open={deleting !== null} onClose={() => setDeleting(null)} title="Delete this folder?">
        <p>
          {deleting ? `"${deleting.name}"` : ""} will be deleted. A folder that still contains files or folders cannot be deleted;
          empty it first.
        </p>
        <div className="shared-folders__modal-actions">
          <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
          <Button variant="danger" loading={busy} onClick={() => void deleteFolder()}>Delete folder</Button>
        </div>
      </Modal>

      {sharing && <MembersModal folder={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}

function MembersModal({ folder, onClose }: { folder: Folder; onClose: () => void }) {
  const { toast } = useToast();
  const [members, setMembers] = useState<Member[]>([]);
  const [people, setPeople] = useState<{ id: string; displayName: string }[]>([]);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<"member" | "viewer">("member");

  const reload = useCallback(() => {
    api
      .get<{ members: Member[] }>(`/folders/${folder.id}/members`)
      .then((res) => setMembers(res.members))
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not load access.", { tone: "danger" }));
  }, [folder.id, toast]);

  useEffect(() => {
    reload();
    api
      .get<{ people: { id: string; displayName: string }[] }>("/people")
      .then((res) => setPeople(res.people))
      .catch(() => undefined);
  }, [reload]);

  const setGrant = async (id: string, next: "member" | "viewer") => {
    try {
      await api.put(`/folders/${folder.id}/members/${id}`, { role: next });
      reload();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not update access.", { tone: "danger" });
    }
  };
  const revoke = async (id: string) => {
    try {
      await api.delete(`/folders/${folder.id}/members/${id}`);
      reload();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not remove access.", { tone: "danger" });
    }
  };

  return (
    <Modal open onClose={onClose} title={`Access to "${folder.name}"`}>
      <p className="shared-folders__note">The owner and office admins always have full access.</p>
      <ul className="shared-folders__list">
        {members.map((m) => (
          <li key={m.userId} className="shared-folders__row">
            <span className="shared-folders__name">{m.displayName}</span>
            <Select aria-label={`Role for ${m.displayName}`} value={m.role} onChange={(e) => void setGrant(m.userId, e.target.value === "viewer" ? "viewer" : "member")}>
              <option value="member">Can edit</option>
              <option value="viewer">Can view</option>
            </Select>
            <Button size="sm" variant="ghost" onClick={() => void revoke(m.userId)}>Remove</Button>
          </li>
        ))}
      </ul>
      <div className="shared-folders__bar">
        <Select aria-label="Person" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Add a person…</option>
          {people
            .filter((p) => p.id !== folder.ownerId && !members.some((m) => m.userId === p.id))
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
        </Select>
        <Select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value === "viewer" ? "viewer" : "member")}>
          <option value="member">Can edit</option>
          <option value="viewer">Can view</option>
        </Select>
        <Button size="sm" disabled={!userId} onClick={() => { void setGrant(userId, role); setUserId(""); }}>
          Add
        </Button>
      </div>
    </Modal>
  );
}
